import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useCurrentUser } from "../CurrentUserContext";
import Food from "../data/food";
import { FoodUnit } from "../data/foodUnit";
import { useNavigate } from "react-router-dom";
import {
  AvailabilityWithEditedFlags,
  isDeleted,
  isNew,
} from "../utils/editUtils";
import { NumberInput } from "./ui/Input";
import { computeCalories } from "../utils/caloriesUtil";
import {
  completeFood,
  completeFoodFromUrl,
  createFood,
  createFoodFromDish,
  deleteFood as deleteFoodRequest,
  FoodDto,
  getDishByFood,
  scanLabel,
  updateFood,
} from "../api";
import { useShops } from "../queries/queries";
import {
  invalidateFoods,
  invalidatePurchases,
  seedPurchase,
  useFoodPurchases,
  usePurchase,
} from "../queries/caches";
import { useQueryClient } from "@tanstack/react-query";
import { CreatePurchaseDrawer } from "./CreatePurchaseDrawer";
import { EditPurchaseDrawer } from "./EditPurchaseDrawer";
import {
  DishPurchaseDrawer,
  DishPurchaseMode,
} from "./dish/DishPurchaseDrawer";
import { DishEditor } from "./DishEditor";
import { DishModification, emptyDish } from "../data/dishModification";
import { Purchase } from "../data/purchase";
import { buildDishFood } from "../utils/dishUtils";
import { useUnloadAlert } from "./useUnloadAlert";
import { deepEquals } from "../utils/equalUtils";
import { showValidationMessage } from "./showValidationMessage";
import { canBeDeleted } from "../utils/entityUtils";
import {
  AvailabilityDrawer,
  useAvailabilityDrawer,
} from "./AvailabilityDrawer";
import { ConfirmModal } from "./ui/ConfirmModal";
import { FoodBasicsCard } from "./food/FoodBasicsCard";
import { FoodNutritionCard } from "./food/FoodNutritionCard";
import { AvailabilityCard } from "./AvailabilityCard";
import { FoodPurchasesCard } from "./food/FoodPurchasesCard";
import { getLowerCaseBaseUnit } from "./NutritionInput";
import { useUpdate } from "../utils/useUpdate";
import { useToast } from "./ui/Toast";
import { Card } from "./ui/Card";
import { Checkbox } from "./ui/Checkbox";
import { Input } from "./ui/Input";
import { Button } from "./ui/Button";
import { Icon } from "./ui/Icon";
import { plan2plateIcons } from "../utils/iconUtils";
import { StringInput } from "./StringInput";
import { AddDashedButton } from "./ui/AddDashedButton";
import { Glyph, SmallIconButton } from "./SmallIconButton";
import { SectionLabel } from "./ui/SectionLabel";
import { Hint } from "./ui/Hint";
import { DrawerHistoryContext } from "../contexts";
import { usePlan2PlateDisclosure } from "./Plan2PlateDrawer";
import { AiSourceSelectionDrawer } from "./AiSourceSelectionDrawer";
import { AiUrlImportDrawer } from "./AiUrlImportDrawer";

export type FoodEditorProps = {
  initialFood: Food;
  isCreate: boolean;
  afterSave?: (createdFood: Food) => void | Promise<void>;
  onClose?: () => void;
  allowUnitDeletion: boolean;
};

function hasDefaultUnit(editedFood: Food | undefined) {
  return editedFood?.foodUnits.find((fu) => fu.isDefault) !== undefined;
}

export const FoodEditor = (props: FoodEditorProps) => {
  const { initialFood, isCreate, afterSave, onClose, allowUnitDeletion } =
    props;
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [nutriValuesPerPortion, setNutriValuesPerPortion] =
    useState<boolean>(false);
  const [editingUnitIndex, setEditingUnitIndex] = useState<number | null>(null);
  const [showCreatePurchase, setShowCreatePurchase] = useState(false);
  const [showEditPurchase, setShowEditPurchase] = useState(false);
  const [editPurchase, setEditPurchase] = useState<Purchase | undefined>();

  const {
    editedObject: editedFood,
    updateEditedObjectField: updateEditedFoodField,
    updateEditedObject: updateEditedFood,
  } = useUpdate<Food>(initialFood);

  const shops = useShops();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const purchases = useFoodPurchases([editedFood.id]);

  // Suspender Lookup des Standard-Kaufprodukts: stellt sicher, dass das Default-
  // Purchase geladen ist, bevor der Editor ihn (z. B. für den Stift) benötigt.
  const defaultPurchaseFromCache = usePurchase(
    editedFood.defaultPurchaseId > 0 ? editedFood.defaultPurchaseId : undefined,
  );
  const defaultPurchase =
    defaultPurchaseFromCache ?? purchases.find((p) => p.isDefault);

  const fromDish = !!editedFood.fromDish;
  const isDishCreate = isCreate && fromDish;
  const isFromDishFood = !isCreate && fromDish;

  const [showDishPurchase, setShowDishPurchase] = useState(false);
  const [dishPurchaseMode, setDishPurchaseMode] =
    useState<DishPurchaseMode>("add");
  const [linkedDish, setLinkedDish] = useState<DishModification | undefined>();

  const dishDraftRef = useRef<DishModification | undefined>(undefined);
  const dishFoodsRef = useRef<FoodDto[]>([]);

  const dishCreateInitial = useMemo<DishModification>(
    () => ({ ...emptyDish(), purchaseRelevant: false, fromCookbook: false }),
    [],
  );
  const emptyPersonLookup = useMemo(() => ({}), []);

  useEffect(() => {
    if (!isFromDishFood || editedFood.id <= 0) {
      return;
    }
    getDishByFood({ path: { foodId: String(editedFood.id) } })
      .then((response) => setLinkedDish(response.data))
      .catch(() => setLinkedDish(undefined));
  }, [isFromDishFood, editedFood.id]);

  const availabilityDrawerProps = useAvailabilityDrawer(
    async (availabilities) => {
      updateEditedFood((foodDraft) => {
        foodDraft.availabilities = availabilities.filter((a) => !isDeleted(a));
      });
      return false;
    },
    false,
  );

  const drawerHistoryContext = useContext(DrawerHistoryContext);
  const aiSourceDisclosure = usePlan2PlateDisclosure(
    drawerHistoryContext,
    "food-ai-source",
  );
  const aiUrlDisclosure = usePlan2PlateDisclosure(
    drawerHistoryContext,
    "food-ai-url",
  );
  const [isAnalyzingUrl, setIsAnalyzingUrl] = useState(false);
  const { expensive } = useCurrentUser();

  const handleAddPortionClick = () => {
    updateEditedFood((draft) => {
      draft.foodUnits.push({
        id: -1,
        description: "",
        netWeight: 0,
        grossWeight: 0,
        isDefault: false,
      });
    });
    setEditingUnitIndex(editedFood.foodUnits.length);
  };

  const handleQuickSelect = (type: string) => {
    if (editingUnitIndex === null) return;

    let description = "";
    let weight = 0;

    switch (type) {
      case "1g":
        description = `${unit}`;
        weight = 1;
        break;
      case "Stück":
        description = "Stück";
        break;
      case "Scheibe":
        description = "Scheibe";
        break;
      case "Portion":
        description = "Portion";
        break;
      case "Handvoll":
        description = "Handvoll";
        break;
    }

    updateEditedFood((draft) => {
      const u = draft.foodUnits[editingUnitIndex];
      if (u) {
        u.description = description;
        u.netWeight = weight;
        u.grossWeight = weight;
      }
    });
  };

  const removeFoodUnit = (index: number) => {
    updateEditedFood((foodDraft) => {
      foodDraft.foodUnits.splice(index, 1);
    });
    if (editingUnitIndex === index) {
      setEditingUnitIndex(null);
    } else if (editingUnitIndex !== null && editingUnitIndex > index) {
      setEditingUnitIndex(editingUnitIndex - 1);
    }
  };

  const updateFoodUnitField = <K extends keyof FoodUnit>(
    field: K,
    value: FoodUnit[K],
  ) => {
    if (editingUnitIndex === null) return;
    updateEditedFood((draft) => {
      const u = draft.foodUnits[editingUnitIndex];
      if (u) {
        u[field] = value;
        // If this is set as default, clear default from all other units
        if (field === "isDefault" && value === true) {
          draft.foodUnits.forEach((fu, idx) => {
            if (idx !== editingUnitIndex) {
              fu.isDefault = false;
            }
          });
        }
      }
    });
  };

  const addPurchase = () => {
    if (fromDish) {
      setDishPurchaseMode("add");
      setShowDishPurchase(true);
      return;
    }
    setShowCreatePurchase(true);
  };

  const editDefaultPurchase = () => {
    if (fromDish) {
      setDishPurchaseMode("default");
      setShowDishPurchase(true);
      return;
    }
    if (defaultPurchase) {
      setEditPurchase(defaultPurchase);
      setShowEditPurchase(true);
    }
  };

  const editPurchaseById = (purchaseId: number) => {
    const purchase = purchases.find((p) => p.id === purchaseId);
    if (purchase) {
      setEditPurchase(purchase);
      setShowEditPurchase(true);
    }
  };

  const saveDishFood = async () => {
    const dish = dishDraftRef.current;
    const foods = dishFoodsRef.current;
    if (!editedFood.description?.trim()) {
      showValidationMessage(toast, "Bitte eine Bezeichnung vergeben.");
      return false;
    }
    if (!dish || !dish.steps.some((s) => s.ingredients.length > 0)) {
      showValidationMessage(toast, "Bitte mindestens eine Zutat angeben.");
      return false;
    }
    const dishForSave: DishModification = {
      ...dish,
      id: undefined,
      description: editedFood.description,
      purchaseRelevant: false,
      foodId: undefined,
    };
    const foodForSave = buildDishFood(editedFood, dishForSave, foods);
    if (!foodForSave.foodUnits[0]?.netWeight) {
      showValidationMessage(toast, "Das Gericht hat kein Gewicht.");
      return false;
    }
    const response = await createFoodFromDish({
      body: { food: foodForSave, dish: dishForSave },
    });
    invalidateFoods(queryClient);
    afterSave && afterSave(response.data);
    setPristine(true);
    return true;
  };

  const save = async () => {
    if (isDishCreate) {
      return saveDishFood();
    }
    if (!hasDefaultUnit(editedFood) && editedFood.purchaseRelevant) {
      showValidationMessage(toast, "Keine Einkauf-Einheit definiert");
      return false;
    } else if (
      editedFood.purchaseRelevant &&
      (!editedFood.availabilities || editedFood.availabilities.length === 0)
    ) {
      toast({
        title: "Keine Verfügbarkeiten definiert",
        status: "error",
        isClosable: true,
      });
      return false;
    } else if (!editedFood.foodUnits.length) {
      toast({
        title: "Keine Einheiten definiert",
        status: "error",
        isClosable: true,
      });
      return false;
    } else {
      const foodUnitsWithInvalidDescription = editedFood.foodUnits.filter(
        (fu) => fu.description.length > 10,
      );
      if (foodUnitsWithInvalidDescription.length > 0) {
        const descriptions = foodUnitsWithInvalidDescription.map(
          (fu) => fu.description,
        );
        toast({
          title: `Folgende Einheiten haben eine zu lange Bezeichnung (>10): ${descriptions.join()}`,
          status: "error",
          isClosable: true,
        });
        return false;
      } else {
        const foodUnitsWithInvalidWeight = editedFood.foodUnits.filter(
          (fu) => (fu.netWeight || 0) <= 0 || (fu.grossWeight || 0) <= 0,
        );
        if (foodUnitsWithInvalidWeight.length) {
          const descriptions = foodUnitsWithInvalidWeight.map(
            (fu) => fu.description || "Unbenannt",
          );
          toast({
            title: `Folgende Einheiten haben ein ungültiges Gewicht: ${descriptions.join()}`,
            status: "error",
            isClosable: true,
          });
          return false;
        }
        let factor: number | undefined;
        if (nutriValuesPerPortion) {
          factor = editedFood.foodUnits.find(
            (fu) => fu.description === "Portion",
          )?.netWeight;
          if (!factor) {
            toast({
              title: "Portion für Nährwerte ist nicht definiert",
              status: "error",
              isClosable: true,
            });
            return false;
          }
        } else {
          factor = 100;
        }
        const foodForSave = {
          ...editedFood,
          fat: (editedFood.fat * 100) / factor,
          carbs: (editedFood.carbs * 100) / factor,
          protein: (editedFood.protein * 100) / factor,
          kcal: editedFood.kcal ? (editedFood.kcal * 100) / factor : undefined,
        };
        const axiosResponse = isCreate
          ? await createFood({ body: foodForSave })
          : await updateFood({ body: foodForSave });
        // Food-Cache aktualisieren, damit z. B. der DishEditor neue FoodUnits
        // sofort sieht (nicht nur die alte, gecachte Food).
        invalidateFoods(queryClient);
        afterSave && afterSave(axiosResponse.data);
        setPristine(true);
        return true;
      }
    }
  };

  const { pristine, setPristine } = useUnloadAlert();

  useEffect(() => {
    setPristine(deepEquals(initialFood, editedFood));
  }, [editedFood, initialFood, setPristine]);

  const editAvailabilities = () => {
    availabilityDrawerProps.editAvailabilities(
      editedFood.foodUnits.find((fu) => fu.isDefault)?.id || -1,
      editedFood.availabilities || [],
    );
  };

  const doComputeCalories = () => {
    const calories = computeCalories(editedFood);
    updateEditedFoodField("kcal", calories);
  };

  const unit = getLowerCaseBaseUnit(editedFood.baseUnit);

  const updateNutriValuesPerPortion = (checked: boolean) => {
    setNutriValuesPerPortion(checked);
    updateEditedFood((foodDraft) => {
      if (checked) {
        let portion = foodDraft.foodUnits.find(
          (fu) => fu.description === "Portion",
        );
        if (!portion) {
          portion = {
            id: -1,
            description: "Portion",
            netWeight: 0,
            grossWeight: 0,
            isDefault: true,
            isAutoCreate: false,
          };
          foodDraft.foodUnits.push(portion);
        }
        portion.isDefault = true;
        for (const foodUnit of foodDraft.foodUnits) {
          if (foodUnit !== portion) {
            foodUnit.isDefault = false;
          }
        }
      }
    });
  };

  const baseline = nutriValuesPerPortion ? "pro Portion" : `pro 100${unit}`;

  const [isOpen, setOpen] = useState(false);
  const deleteFood = () => {
    setOpen(true);
  };

  const availabilitySummary = useMemo(
    () =>
      (editedFood.availabilities || [])
        .filter((a) => !isDeleted(a as unknown as AvailabilityWithEditedFlags))
        .map((a) => ({
          shopName: shops.find((s) => s.id === a.shopId)?.description ?? "",
          department: a.department
            ? {
                name: a.department.name,
                description: a.department.description,
              }
            : undefined,
        }))
        .filter((e) => e.shopName),
    [editedFood.availabilities, shops],
  );

  const complete = async () => {
    const result = (await completeFood({ body: editedFood })).data;
    updateEditedFood(result);
  };

  // Werte aus einer URL ableiten: Backend lädt die Seite, konvertiert sie nach
  // Markdown und lässt die KI daraus die Nährwerte ableiten.
  const analyzeUrl = async (url: string) => {
    setIsAnalyzingUrl(true);
    try {
      const response = await completeFoodFromUrl({
        body: { url, food: editedFood },
      });
      updateEditedFood(response.data);
      aiUrlDisclosure.onClose();
      aiSourceDisclosure.onClose();
    } catch {
      toast({
        title: "Lebensmittel konnte nicht aus der URL geladen werden",
        status: "error",
        isClosable: true,
      });
    } finally {
      setIsAnalyzingUrl(false);
    }
  };

  // Nährwerte aus einem Foto (z. B. Nährwerttabelle) ableiten.
  const handleImageScan = async (file: File) => {
    try {
      const result = (await scanLabel({ body: { file } })).data;
      updateEditedFood(result);
    } catch {
      toast({
        title: "Foto konnte nicht ausgewertet werden",
        status: "error",
        isClosable: true,
      });
    }
  };

  // Inline validation for portion edit panel
  const handlePortionSave = () => {
    if (editingUnitIndex === null) return;
    const fu = editedFood.foodUnits[editingUnitIndex];
    if (!fu.description.trim()) {
      showValidationMessage(toast, "Die Bezeichnung darf nicht leer sein.");
      return;
    }
    if ((fu.netWeight || 0) <= 0 || (fu.grossWeight || 0) <= 0) {
      showValidationMessage(
        toast,
        "Netto- und Bruttogewicht müssen größer als 0 sein.",
      );
      return;
    }
    if (fu.description.length > 10) {
      showValidationMessage(
        toast,
        "Die Bezeichnung darf maximal 10 Zeichen lang sein.",
      );
      return;
    }
    setEditingUnitIndex(null);
  };

  const handlePortionCancel = () => {
    if (editingUnitIndex === null) return;
    const fu = editedFood.foodUnits[editingUnitIndex];
    // If it's a new portion and hasn't been configured, discard it
    if (!fu.description && !fu.netWeight && !fu.grossWeight && fu.id === -1) {
      removeFoodUnit(editingUnitIndex);
    }
    setEditingUnitIndex(null);
  };

  return (
    <>
      <ConfirmModal
        open={isOpen}
        onCancel={() => setOpen(false)}
        onConfirm={async () => {
          await deleteFoodRequest({ path: { foodId: editedFood.id } });
          setOpen(false);
          navigate(`/food`);
        }}
        icon="delete"
        destructive
        confirmLabel="Löschen"
        title="Wirklich löschen?"
      />
      <AvailabilityDrawer {...availabilityDrawerProps} />
      <AiSourceSelectionDrawer
        open={aiSourceDisclosure.isOpen}
        onClose={aiSourceDisclosure.onClose}
        onSelectDescription={() => {
          aiSourceDisclosure.onClose();
          complete();
        }}
        onSelectUrl={() => {
          aiUrlDisclosure.onOpen();
        }}
        onSelectImage={handleImageScan}
        showDescriptionOption={!!editedFood.description?.trim()}
      />
      <AiUrlImportDrawer
        open={aiUrlDisclosure.isOpen}
        onClose={aiUrlDisclosure.onClose}
        onAnalyze={analyzeUrl}
        isLoading={isAnalyzingUrl}
      />
      <CreatePurchaseDrawer
        open={showCreatePurchase}
        onClose={() => setShowCreatePurchase(false)}
        onSubmit={() => setShowCreatePurchase(false)}
        food={editedFood}
      />
      {!fromDish && editPurchase && (
        <EditPurchaseDrawer
          open={showEditPurchase}
          onClose={() => setShowEditPurchase(false)}
          purchase={editPurchase}
          food={editedFood}
          onSubmit={(updatedPurchase) => {
            seedPurchase(queryClient, updatedPurchase);
            invalidatePurchases(queryClient);
            // Nährwerte des Standard-Kaufprodukts sind die des Lebensmittels.
            if (updatedPurchase.isDefault) {
              updateEditedFood((draft) => {
                draft.fat = updatedPurchase.fat;
                draft.carbs = updatedPurchase.carbs;
                draft.protein = updatedPurchase.protein;
                draft.kcal = updatedPurchase.kcal;
              });
            }
          }}
        />
      )}
      {isFromDishFood && linkedDish && (
        <DishPurchaseDrawer
          open={showDishPurchase}
          onClose={() => setShowDishPurchase(false)}
          food={editedFood}
          initialDish={linkedDish}
          mode={dishPurchaseMode}
          onSaved={(updatedFood, savedDish) => {
            if (updatedFood) {
              updateEditedFood(updatedFood);
            }
            if (savedDish) {
              setLinkedDish(savedDish);
            }
          }}
        />
      )}

      <div className="flex flex-col gap-5 w-full">
        {/* Name Input with scan feature */}
        <Card className="p-3">
          <div className="flex w-full items-center gap-2">
            <StringInput
              fieldName="description"
              width={"100%"}
              value={editedFood.description}
              updater={updateEditedFoodField}
              placeholder="Bezeichnung"
              dataCy={"food-description-input"}
            />
            {expensive && (
              <div className="flex items-center h-[40px] self-end">
                <SmallIconButton
                  data-cy="food-ai-wand-button"
                  icon="magicWandSparkles"
                  label="Nährwerte ableiten"
                  onClick={() => aiSourceDisclosure.onOpen()}
                />
              </div>
            )}
          </div>

          <div className="mt-2 flex flex-col gap-2">
            {!fromDish && (
              <label className="flex cursor-pointer items-center gap-3 rounded-card border border-line bg-surface p-2.5">
                <Checkbox
                  data-cy="food-base-unit-ml-checkbox"
                  checked={editedFood.baseUnit === "ML"}
                  onCheckedChange={(checked) => {
                    updateEditedFoodField("baseUnit", checked ? "ML" : "G");
                  }}
                />
                <span className="text-sm font-medium text-ink">
                  Ist in ml (Flüssigkeit)
                </span>
              </label>
            )}
            {(isCreate || fromDish) && (
              <label
                className={`flex items-center gap-3 rounded-card border border-line bg-surface p-2.5 ${
                  isCreate ? "cursor-pointer" : "opacity-60"
                }`}
              >
                <Checkbox
                  data-cy="food-from-dish-checkbox"
                  disabled={!isCreate}
                  checked={fromDish}
                  onCheckedChange={(checked) => {
                    updateEditedFood((draft) => {
                      draft.fromDish = !!checked;
                      if (checked) {
                        draft.purchaseRelevant = false;
                      }
                    });
                  }}
                />
                <span className="text-sm font-medium text-ink">
                  Aus Gericht
                </span>
              </label>
            )}
            {isCreate && !fromDish && (
              <label className="flex cursor-pointer items-center gap-3 rounded-card border border-line bg-surface p-2.5">
                <Checkbox
                  data-cy="food-purchase-relevant-checkbox"
                  checked={editedFood.purchaseRelevant}
                  onCheckedChange={(checked) => {
                    updateEditedFoodField("purchaseRelevant", checked);
                  }}
                />
                <span className="text-sm font-medium text-ink">
                  Einkaufsliste-Relevant
                </span>
              </label>
            )}
            {!editedFood.purchaseRelevant && (
              <div className="flex flex-col gap-1.5 mt-2">
                <Input
                  data-cy="food-vendor-input"
                  placeholder="Lokal"
                  value={editedFood.vendor || ""}
                  onChange={(e) =>
                    updateEditedFoodField("vendor", e.target.value)
                  }
                />
              </div>
            )}
            {isCreate && (
              <Hint className="mt-2 rounded-card border border-line bg-surface p-3 text-xs text-ink-muted">
                {unit == "g" && editedFood.purchaseRelevant && (
                  <p>
                    Du kaufst dieses Lebensmittel selbst ein, z. B. Nudeln, Käse
                    oder Brot. Wenn du eine Einkaufsliste erstellst, wird es
                    dort mit seiner benötigten Menge berücksichtigt.
                  </p>
                )}
                {unit == "ml" && editedFood.purchaseRelevant && (
                  <p>
                    Du kaufst dieses Getränk oder flüssiges Lebensmittel selbst
                    ein, z. B. Milch, Saft oder Öl. Wenn du eine Einkaufsliste
                    erstellst, wird es dort mit seiner benötigten Menge in ml
                    berücksichtigt.
                  </p>
                )}
                {unit == "g" && !editedFood.purchaseRelevant && (
                  <p>
                    Du bestellst dieses Gericht im Restaurant oder beim
                    Lieferdienst, z. B. Pizza, Burger oder Pasta. Du kannst das
                    Lokal hinterlegen, bei dem du es bestellst.
                  </p>
                )}
                {unit == "ml" && !editedFood.purchaseRelevant && (
                  <p>
                    Du bestellst dieses Getränk im Restaurant oder beim
                    Lieferdienst, z. B. einen Cocktail oder eine Limonade. Du
                    kannst das Lokal hinterlegen, bei dem du es bestellst.
                  </p>
                )}
              </Hint>
            )}
          </div>
        </Card>

        {/* Nährwerte pro 100g / pro Portion */}
        {!isDishCreate && (
          <FoodNutritionCard
            food={editedFood}
            isCreate={isCreate}
            nutriValuesPerPortion={nutriValuesPerPortion}
            baseline={baseline}
            defaultPurchase={defaultPurchase}
            onFieldChange={updateEditedFoodField}
            onTogglePerPortion={updateNutriValuesPerPortion}
            onComputeCalories={doComputeCalories}
            onEditDefaultPurchase={editDefaultPurchase}
          />
        )}

        {/* Aus Gericht: schlichter Dish-Form direkt im Editor (nur beim Erstellen) */}
        {isDishCreate && (
          <DishEditor
            isCreate
            variant="simple"
            hideDescription
            hideSaveButton
            initialDish={dishCreateInitial}
            personLookup={emptyPersonLookup}
            loadAvailabilities={async () => {}}
            onDishChange={(dish, foods) => {
              dishDraftRef.current = dish;
              dishFoodsRef.current = foods;
            }}
          />
        )}

        {/* Einheiten Section */}
        {!isDishCreate && !isFromDishFood && (
          <Card className="flex flex-col gap-4 p-4 border-line bg-surface-card">
            <div className="flex flex-col">
              <SectionLabel>Einheiten</SectionLabel>
              <span className="text-xs text-ink-muted">
                Welche Einheiten sollen beim Planen verfügbar sein?
              </span>
            </div>
            {/* Portion Pills Row */}
            {!!editedFood.foodUnits.length && (
              <div className="flex flex-wrap gap-2 items-center">
                {editedFood.foodUnits.map((foodUnit, idx) => {
                  const isEditing = editingUnitIndex === idx;
                  const isDefault = foodUnit.isDefault;
                  const displayLabel = foodUnit.description
                    ? `${foodUnit.description}`
                    : "Einheit...";
                  return (
                    <div
                      key={idx}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-semibold transition ${
                        isEditing
                          ? "border-gold bg-gold/10 text-gold"
                          : "border-line bg-surface text-ink"
                      }`}
                    >
                      <span
                        className="cursor-pointer select-none"
                        onClick={() => setEditingUnitIndex(idx)}
                      >
                        {displayLabel}
                      </span>
                      {isDefault && editedFood.purchaseRelevant && (
                        <span className="px-1.5 py-0.5 bg-teal/15 text-teal text-2xs font-bold rounded-xs">
                          EK
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Portion Editor Box */}
            {editingUnitIndex !== null && (
              <div className="border border-gold rounded-card p-4 flex flex-col gap-4 mt-2 bg-surface/20">
                <div className="flex flex-col gap-1.5">
                  <div className="flex flex-wrap gap-2 mt-1">
                    {[
                      `1${unit}`,
                      "Stück",
                      "Scheibe",
                      "Portion",
                      "Handvoll",
                    ].map((opt) => {
                      // map visual name to switch case keys
                      const valueMap: Record<string, string> = {
                        [`1g`]: "1g",
                        [`1ml`]: "1g",
                      };
                      const typeKey = valueMap[opt] || opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => handleQuickSelect(typeKey)}
                          className="px-3 py-1.5 rounded-full border border-line bg-surface text-xs font-semibold text-ink-muted hover:bg-surface-card transition select-none cursor-pointer"
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-ink-muted">
                    Bezeichnung
                  </label>
                  <Input
                    data-cy="food-unit-description-input"
                    placeholder="Portionsbezeichnung (z.B. Stück, Scheibe)"
                    value={
                      editedFood.foodUnits[editingUnitIndex].description || ""
                    }
                    onChange={(e) =>
                      updateFoodUnitField("description", e.target.value)
                    }
                    disabled={
                      editedFood.foodUnits[editingUnitIndex].isAutoCreate
                    }
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-ink-muted">
                      Netto ({unit})
                    </label>
                    <NumberInput
                      data-cy="food-unit-net-weight-input"
                      value={editedFood.foodUnits[editingUnitIndex].netWeight}
                      onValueChange={(val) =>
                        updateFoodUnitField("netWeight", val)
                      }
                      disabled={
                        !isNew(editedFood.foodUnits[editingUnitIndex]) ||
                        editedFood.foodUnits[editingUnitIndex].isAutoCreate
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-ink-muted">
                      Brutto ({unit})
                    </label>
                    <NumberInput
                      data-cy="food-unit-gross-weight-input"
                      value={editedFood.foodUnits[editingUnitIndex].grossWeight}
                      onValueChange={(val) =>
                        updateFoodUnitField("grossWeight", val)
                      }
                      disabled={
                        !isNew(editedFood.foodUnits[editingUnitIndex]) ||
                        editedFood.foodUnits[editingUnitIndex].isAutoCreate
                      }
                    />
                  </div>
                </div>

                {editedFood.purchaseRelevant && (
                  <label className="flex items-center gap-3 cursor-pointer select-none mt-1">
                    <Checkbox
                      checked={editedFood.foodUnits[editingUnitIndex].isDefault}
                      onCheckedChange={(checked) =>
                        updateFoodUnitField("isDefault", checked)
                      }
                    />
                    <span className="text-sm text-ink">
                      Einkauf-Einheit (für Einkaufsliste)
                    </span>
                  </label>
                )}

                <div className="flex justify-center gap-3 border-t border-line/50 pt-3 mt-1">
                  {(allowUnitDeletion ||
                    isNew(editedFood.foodUnits[editingUnitIndex])) && (
                    <SmallIconButton
                      label="Einheit löschen"
                      icon="delete"
                      variant="destructive"
                      onClick={() => removeFoodUnit(editingUnitIndex)}
                    />
                  )}
                  <SmallIconButton
                    label="Einheit speichern"
                    icon="check"
                    className={"!bg-teal [&_polyline]:!stroke-white"}
                    onClick={handlePortionSave}
                  />
                </div>
              </div>
            )}
            {/* Add Portion Button Pill */}
            <div data-cy="food-add-unit-button">
              <AddDashedButton
                dataCy="dish-add-step-button"
                onClick={handleAddPortionClick}
              >
                <Glyph icon="plus" size={15} />
                <span>Einheit hinzufügen</span>
              </AddDashedButton>
            </div>
          </Card>
        )}

        {/* Verfügbarkeit Section */}
        {editedFood.purchaseRelevant && (
          <AvailabilityCard
            entries={availabilitySummary}
            onClick={editAvailabilities}
          />
        )}

        {/* Produkte (existing brand purchases) Section for edit mode */}
        {!isCreate && purchases.length > 0 && (
          <FoodPurchasesCard
            purchases={purchases.filter((p) => !p.isDefault)}
            onEdit={fromDish ? undefined : editPurchaseById}
            onAdd={addPurchase}
          />
        )}

        {/* Delete Food Button */}
        {editedFood.canBeDeleted && !isNew(editedFood) && (
          <Button
            variant="destructive"
            onClick={deleteFood}
            className="flex items-center justify-center gap-2 mt-2 w-full py-3 h-12"
          >
            <Icon size={16} tone="protein">
              {plan2plateIcons.delete}
            </Icon>
            Lebensmittel löschen
          </Button>
        )}

        {/* Sticky/Fixed bottom save button block */}
        <div className="mt-4">
          <Button
            fullWidth
            variant="primary"
            size="lg"
            dataCy="food-save-button"
            disabled={!isCreate && pristine}
            onClick={() => {
              // Im Erstellen-Drawer ohne Änderungen einfach schließen
              if (isCreate && pristine && onClose) {
                onClose();
                return;
              }
              save();
            }}
            className="bg-teal hover:brightness-110 text-white font-bold text-base flex items-center justify-center gap-2"
          >
            ✓ Lebensmittel speichern
          </Button>
        </div>
      </div>
    </>
  );
};
